import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Collapse, Divider, Drawer, DrawerBody, DrawerContent, DrawerOverlay, HStack, IconButton, Link, Text, VStack, useDisclosure } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button, Icon, Image, ModalBase, NavLink } from '@/components';
import { useNmibleStatus } from '@/hooks';
import { CONFIG_NAVIGATION_TO_ROUTE } from '@/routes';
import { useAuth, useConfig } from '@/store';
import { APP_CONFIG, DataTest } from '@/types';
import { resolveWebViewUrl } from '@/utils';
const { ConfigComponentType, NavigationComponentType, PredefinedScreen } = APP_CONFIG.COMPONENTS;
const ALWAYS_HIDDEN_SCREENS = [
    PredefinedScreen.SCHEDULE,
    PredefinedScreen.TELEHEALTH,
    PredefinedScreen.QUESTIONNAIRE_FEED
];
const isVisibleForPhase = (item, phase) => { var _a; return !((_a = item.phases) === null || _a === void 0 ? void 0 : _a.length) || item.phases.includes(phase); };
// Filters nav items based on the phase. For categories, we also filter options
const filterByPhase = (items, phase) => items
    .filter((item) => isVisibleForPhase(item, phase))
    .map((item) => item.type === NavigationComponentType.CATEGORY && item.options
    ? Object.assign(Object.assign({}, item), { options: item.options.filter((opt) => isVisibleForPhase(opt, phase)) }) : item);
export const MobileMenu = () => {
    var _a;
    const { t } = useTranslation();
    const { isOpen: isMenuOpen, onClose: onMenuClose, onToggle: onMenuToggle } = useDisclosure();
    const { isOpen: isSignOutOpen, onClose: onSignOutClose, onOpen: onSignOutOpen } = useDisclosure();
    const { isOpen: isDialogOpen, onClose: onDialogClose, onOpen: onDialogOpen } = useDisclosure();
    const btnRef = useRef(null);
    const location = useLocation();
    const { getComponentConfig, getNavigationItems } = useConfig();
    const { handleLogout, state: { currentUserPhase } } = useAuth();
    const { primary_navigation = [], secondary_navigation = [] } = (_a = getComponentConfig(ConfigComponentType.NAVIGATION)) !== null && _a !== void 0 ? _a : {};
    const hasPaymentNav = getNavigationItems().some((item) => item.type === APP_CONFIG.COMPONENTS.NavigationComponentType.PREDEFINED &&
        item.screen === PredefinedScreen.PAYMENT);
    const { nmibleStatus, nmibleLoading } = useNmibleStatus({ enabled: hasPaymentNav });
    const hiddenScreens = useMemo(() => {
        const screens = new Set(ALWAYS_HIDDEN_SCREENS);
        if (nmibleLoading || !nmibleStatus) {
            screens.add(PredefinedScreen.PAYMENT);
            screens.add(PredefinedScreen.PAYMENT_SETUP);
        }
        return screens;
    }, [nmibleLoading, nmibleStatus]);
    const [dialogItem, setDialogItem] = useState(null);
    const handleDialogOpen = (item) => {
        setDialogItem(item);
        onDialogOpen();
    };
    const handleDialogClose = () => {
        onDialogClose();
        setDialogItem(null);
    };
    useEffect(() => {
        if (isMenuOpen)
            onMenuClose();
    }, [location.pathname]);
    const primary = useMemo(() => filterByPhase(primary_navigation, currentUserPhase), [primary_navigation, currentUserPhase]);
    const secondary = useMemo(() => filterByPhase(secondary_navigation, currentUserPhase), [secondary_navigation, currentUserPhase]);
    const [expanded, setExpanded] = useState({});
    const toggleCat = (id) => {
        return setExpanded((s) => { var _a; return (Object.assign(Object.assign({}, s), { [id]: !((_a = s[id]) !== null && _a !== void 0 ? _a : false) })); });
    };
    const renderLeaf = useCallback((item) => {
        var _a, _b;
        const title = t((_a = item.title) !== null && _a !== void 0 ? _a : '');
        const imageSrc = ((_b = item.icon) !== null && _b !== void 0 ? _b : undefined);
        let to;
        let external = false;
        let onClick;
        switch (item.type) {
            case NavigationComponentType.PREDEFINED: {
                const screen = item.screen;
                if (screen && hiddenScreens.has(screen))
                    return null;
                to = screen ? CONFIG_NAVIGATION_TO_ROUTE[screen] : undefined;
                break;
            }
            case NavigationComponentType.WEB_VIEW: {
                const req = item.request;
                if (!req)
                    break;
                to = resolveWebViewUrl(req);
                external = true;
                break;
            }
            case NavigationComponentType.DIALOG: {
                onClick = () => handleDialogOpen(item);
                break;
            }
            case NavigationComponentType.SIGN_OUT: {
                onClick = () => {
                    onSignOutOpen();
                };
                break;
            }
            default:
                break;
        }
        const isActive = Boolean(to && !external && location.pathname.startsWith(to));
        const wrapperStyles = isActive
            ? {
                background: '#19475f',
                borderRadius: '10px'
            }
            : {};
        const Content = (_jsxs(HStack, Object.assign({ h: '48px', mx: '8px' }, { children: [imageSrc ? (_jsx(Image, { alt: '', borderRadius: 'md', boxSize: '20px', objectFit: 'contain', path: imageSrc, pointerEvents: 'none' })) : null, _jsx(Text, Object.assign({ as: 'span', color: 'white', "data-test": DataTest.MOBILE_MENU_LEAF, p: 2 }, { children: title }))] })));
        if (onClick) {
            return (_jsx(Box, Object.assign({ as: 'button', className: 'nav-leaf', onClick: onClick, textAlign: 'left' }, { children: Content }), item.uuid));
        }
        if (to) {
            if (external) {
                return (_jsx(Link, Object.assign({ className: 'nav-leaf', href: to, isExternal: true, onClick: onMenuClose }, wrapperStyles, { children: Content }), item.uuid));
            }
            return (_jsx(NavLink, Object.assign({ className: 'nav-leaf', onClick: onMenuClose, to: to }, wrapperStyles, { children: Content }), item.uuid));
        }
    }, [location.pathname, hiddenScreens, onMenuClose, onSignOutOpen]);
    const renderItem = useCallback((item) => {
        var _a, _b;
        if (item.type === NavigationComponentType.CATEGORY) {
            const title = t((_a = item.title) !== null && _a !== void 0 ? _a : '');
            const options = (item.options || []).filter((opt) => {
                if (opt.type === NavigationComponentType.PREDEFINED) {
                    const screen = opt.screen;
                    if (screen && hiddenScreens.has(screen))
                        return false;
                }
                return true;
            });
            if (options.length === 0)
                return null;
            const isCatOpen = (_b = expanded[item.uuid]) !== null && _b !== void 0 ? _b : false;
            const panelId = `cat-${item.uuid}`;
            return (_jsxs(Box, { children: [_jsxs(HStack, Object.assign({ _hover: { bg: 'whiteAlpha.100' }, align: 'center', "aria-controls": panelId, "aria-expanded": isCatOpen, as: 'button', borderRadius: '10px', "data-test": DataTest.MOBILE_MENU_ITEM, justify: 'space-between', mb: 2, onClick: () => toggleCat(item.uuid), px: 2, w: '100%' }, { children: [_jsx(Text, Object.assign({ color: 'white', textAlign: 'left' }, { children: title })), _jsx(Box, Object.assign({ _rtl: {
                                    transform: isCatOpen ? 'rotate(0deg)' : 'rotate(90deg)'
                                }, transform: isCatOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s ease' }, { children: _jsx(Icon, { color: 'white', name: 'chevron-down', type: 'solid' }) }))] })), _jsx(Collapse, Object.assign({ animateOpacity: true, in: isCatOpen, unmountOnExit: true }, { children: _jsx(VStack, Object.assign({ align: 'stretch', id: panelId, pl: 2, spacing: 4, sx: {
                                '& .nav-leaf': { opacity: 0.7, transition: 'opacity 0.5s ease' }
                            } }, { children: options.map(renderLeaf) })) }))] }, item.uuid));
        }
        return renderLeaf(item);
    }, [expanded, renderLeaf, t, toggleCat]);
    const primaryNodes = useMemo(() => primary.map(renderItem), [primary, expanded, location.pathname, renderItem]);
    const secondaryNodes = useMemo(() => secondary.map(renderItem), [secondary, expanded, location.pathname, renderItem]);
    return (_jsxs(Box, Object.assign({ as: 'nav', zIndex: 9998 }, { children: [_jsx(IconButton, { bg: 'transparent', "aria-label": isMenuOpen ? t('components.navigation.close') : t('components.navigation.open'), "data-test": DataTest.MOBILE_MENU_TOGGLE, icon: _jsx(Icon, { color: 'white', size: '30px', name: isMenuOpen ? 'times' : 'menu' }), onClick: onMenuToggle, ref: btnRef, size: 'md' }), _jsxs(Drawer, Object.assign({ finalFocusRef: btnRef, isOpen: isMenuOpen, onClose: onMenuClose, placement: 'top' }, { children: [_jsx(DrawerOverlay, {}), _jsxs(DrawerContent, Object.assign({ backgroundColor: 'darkBrandBlue.100', h: '100vh', overflow: 'auto', w: '100vw' }, { children: [_jsx(Box, { h: '60px' }), _jsx(DrawerBody, { children: _jsxs(VStack, Object.assign({ align: 'stretch', "data-test": DataTest.MOBILE_MENU_DRAWER, mt: 2, spacing: 2 }, { children: [primaryNodes.length > 0 && (_jsx(VStack, Object.assign({ align: 'stretch', spacing: 4 }, { children: primaryNodes }))), secondaryNodes.length > 0 && (_jsxs(_Fragment, { children: [primaryNodes.length > 0 && _jsx(Divider, { borderColor: 'whiteAlpha.300' }), _jsx(VStack, Object.assign({ align: 'stretch', spacing: 4 }, { children: secondaryNodes }))] }))] })) })] }))] })), _jsx(ModalBase, Object.assign({ "data-test": DataTest.MOBILE_MENU_SIGN_OUT_LEAF, footer: _jsxs(VStack, Object.assign({ align: 'stretch', spacing: 3, w: '100%' }, { children: [_jsx(Button, Object.assign({ "data-test": DataTest.MOBILE_MENU_SIGN_OUT_CONFIRM, onClick: handleLogout, w: '100%' }, { children: t('components.navigation.sign_out_dialog.button_yes') })), _jsx(Button, Object.assign({ "data-test": DataTest.MOBILE_MENU_SIGN_OUT_CANCEL, onClick: onSignOutClose, variant: 'outline', w: '100%' }, { children: t('components.navigation.sign_out_dialog.button_close') }))] })), isOpen: isSignOutOpen, onClose: onSignOutClose, title: t('components.navigation.sign_out') }, { children: _jsx(Text, Object.assign({ align: 'center' }, { children: t('components.navigation.sign_out_dialog.body') })) })), _jsx(ModalBase, Object.assign({ footer: _jsx(VStack, Object.assign({ align: 'stretch', "data-test": DataTest.MOBILE_MENU_DIALOG_LEAF, spacing: 3, w: '100%' }, { children: _jsx(Button, Object.assign({ "data-test": DataTest.MOBILE_MENU_DIALOG_CLOSE, onClick: handleDialogClose, w: '100%' }, { children: t(dialogItem === null || dialogItem === void 0 ? void 0 : dialogItem.button_text) })) })), isCentered: true, isOpen: isDialogOpen, onClose: handleDialogClose, title: t(dialogItem === null || dialogItem === void 0 ? void 0 : dialogItem.title) }, { children: _jsx(Text, Object.assign({ textAlign: 'center' }, { children: t(dialogItem === null || dialogItem === void 0 ? void 0 : dialogItem.body) })) }))] })));
};
